import { memo, useState, type ComponentType, type FC } from "react";
import dayjs from "dayjs";
import { CornerDownRight, FileText, ImageIcon, Info, Check, Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { WhatsAppMediaType } from "src/services/WhatsAppCampaignAPI";
import { MESSAGE_TIME_FORMAT, WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type {
  WhatsAppMediaAttachment,
  WhatsAppTemplateFormErrors,
  WhatsAppTemplateFormValues,
} from "../WhatsAppCampaignTypes";
import { fillSamples, getBodyVariableIndexes } from "../WhatsAppTemplateFormUtils";
import { WhatsAppMessageText } from "./WhatsAppMessageParts";

const PREVIEW_KEY = `${WHATSAPP_EDITOR_KEY}.form.preview`;
const READY_KEY = `${WHATSAPP_EDITOR_KEY}.form.ready`;
const MODES = ["sample", "variables"] as const;

type PreviewMode = (typeof MODES)[number];

const MEDIA_ICONS: Record<WhatsAppMediaType, ComponentType<{ className?: string }>> = {
  image: ImageIcon,
  video: Video,
  document: FileText,
};

interface TemplateFormPreviewProps {
  values: WhatsAppTemplateFormValues;
  errors: WhatsAppTemplateFormErrors;
  attachment: WhatsAppMediaAttachment | null;
}

const TemplateFormPreview: FC<TemplateFormPreviewProps> = ({ values, errors, attachment }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<PreviewMode>("sample");
  const { body, headerText, footer, mediaType, buttons, name, samples } = values;
  const variableCount = getBodyVariableIndexes(body).length;
  const MediaIcon = mediaType ? MEDIA_ICONS[mediaType] : null;
  const render = (text: string) => (mode === "sample" ? fillSamples(text, samples) : text);
  const isEmpty = !body.trim() && !headerText.trim() && !footer.trim() && !MediaIcon;

  const checklist = [
    { key: "nameLanguage", done: Boolean(name.trim()) && !errors.name && !errors.language },
    { key: "body", done: Boolean(body.trim()) && !errors.body },
    { key: "samples", done: variableCount > 0 && errors.samples.length === 0, count: variableCount },
    {
      key: "buttons",
      done: buttons.length > 0 && Object.keys(errors.buttons).length === 0,
      count: buttons.length,
    },
  ];

  return (
    <aside className="wa-form-aside">
      <section className="wa-form-card">
        <header className="wa-form-card__header">
          <div>
            <h3 className="wa-form-section__title">{t(`${PREVIEW_KEY}.title`)}</h3>
            <p className="wa-form-section__subtitle">{t(`${PREVIEW_KEY}.subtitle`)}</p>
          </div>
          <div className="wa-live-preview__modes" role="group" aria-label={t(`${PREVIEW_KEY}.mode`)}>
            {MODES.map(option => (
              <button
                key={option}
                type="button"
                aria-pressed={mode === option}
                className={`wa-live-preview__mode ${
                  mode === option ? "wa-live-preview__mode--active" : ""
                }`}
                onClick={() => setMode(option)}
              >
                {t(`${PREVIEW_KEY}.modes.${option}`)}
              </button>
            ))}
          </div>
        </header>

        <div className="wa-live-preview__device wa-form-preview">
          <div
            className={`wa-live-preview__bubble ${isEmpty ? "wa-form-preview__bubble--empty" : ""}`}
          >
            {isEmpty ? (
              <p className="wa-live-preview__text wa-form-preview__empty" role="status">
                {t(`${PREVIEW_KEY}.empty`)}
              </p>
            ) : (
              <>
                {MediaIcon && (
                  <div className="wa-live-preview__media">
                    {attachment?.previewUrl ? (
                      <img src={attachment.previewUrl} alt="" className="wa-live-preview__image" />
                    ) : (
                      <MediaIcon className="wa-live-preview__placeholder" aria-hidden />
                    )}
                  </div>
                )}
                {!mediaType && headerText && (
                  <WhatsAppMessageText body={render(headerText)} className="wa-live-preview__heading" />
                )}
                {body && <WhatsAppMessageText body={render(body)} className="wa-live-preview__text" />}
                {footer && <p className="wa-live-preview__footer-text">{footer}</p>}
              </>
            )}
            <span className="wa-message-meta">{dayjs().format(MESSAGE_TIME_FORMAT)}</span>
          </div>
          {buttons.map(button => (
            <span key={button.id} className="wa-live-preview__button">
              {button.text}
            </span>
          ))}
        </div>
      </section>

      <section className="wa-form-card">
        <h3 className="wa-form-section__title">{t(`${READY_KEY}.title`)}</h3>
        <ul className="wa-form-ready">
          {checklist.map(item => (
            <li
              key={item.key}
              className={`wa-form-ready__item ${item.done ? "wa-form-ready__item--done" : ""}`}
            >
              {item.done ? (
                <Check size={14} aria-hidden />
              ) : (
                <CornerDownRight size={14} aria-hidden />
              )}
              {t(`${READY_KEY}.${item.key}`, { count: item.count })}
            </li>
          ))}
        </ul>
        <p className="wa-form-ready__note">
          <Info size={14} aria-hidden />
          {t(`${READY_KEY}.note`)}
        </p>
      </section>
    </aside>
  );
};

export default memo(TemplateFormPreview);
